import Phaser from 'phaser';
import { useClockStore } from '@/state/clockStore';
import type { WeatherKind } from '@/network/messages';

const TEX_RAIN = 'fx_rain_drop';
const TEX_SNOW = 'fx_snow_flake';
const TEX_FOG  = 'fx_fog_band';

function ensureTextures(scene: Phaser.Scene): void {
  if (!scene.textures.exists(TEX_RAIN)) {
    const g = scene.add.graphics({ x: 0, y: 0 });
    g.fillStyle(0xbcd6ff, 1);
    g.fillRect(0, 0, 2, 8);
    g.generateTexture(TEX_RAIN, 2, 8);
    g.destroy();
  }
  if (!scene.textures.exists(TEX_SNOW)) {
    const g = scene.add.graphics({ x: 0, y: 0 });
    g.fillStyle(0xffffff, 1);
    g.fillCircle(3, 3, 3);
    g.generateTexture(TEX_SNOW, 6, 6);
    g.destroy();
  }
  if (!scene.textures.exists(TEX_FOG)) {
    const g = scene.add.graphics({ x: 0, y: 0 });
    g.fillStyle(0xffffff, 0.10);
    g.fillRect(0, 0, 256, 128);
    g.generateTexture(TEX_FOG, 256, 128);
    g.destroy();
  }
}

export class WeatherFXSystem {
  private scene: Phaser.Scene;
  private currentWeather: WeatherKind | null = null;
  private rain?: Phaser.GameObjects.Particles.ParticleEmitter;
  private snow?: Phaser.GameObjects.Particles.ParticleEmitter;
  private fog?: Phaser.GameObjects.TileSprite;
  private nextLightning = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    ensureTextures(scene);
    scene.scale.on('resize', this.onResize, this);
  }

  private onResize(size: Phaser.Structs.Size): void {
    if (this.fog) {
      this.fog.setSize(size.width, size.height);
    }
  }

  private clearAll(): void {
    if (this.rain) { this.rain.destroy(); this.rain = undefined; }
    if (this.snow) { this.snow.destroy(); this.snow = undefined; }
    if (this.fog)  { this.fog.destroy();  this.fog  = undefined; }
  }

  private startRain(intensity: 'rain' | 'storm'): void {
    const cam = this.scene.cameras.main;
    const qty = intensity === 'storm' ? 8 : 4;
    this.rain = this.scene.add.particles(0, 0, TEX_RAIN, {
      x: { min: 0, max: cam.width },
      y: -20,
      lifespan: 900,
      speedX: -120,
      speedY: 900,
      quantity: qty,
      frequency: 30,
      alpha: { start: 0.7, end: 0.0 },
      scale: { min: 0.8, max: 1.4 },
      blendMode: Phaser.BlendModes.ADD,
    });
    this.rain.setScrollFactor(0).setDepth(9100);
  }

  private startSnow(): void {
    const cam = this.scene.cameras.main;
    this.snow = this.scene.add.particles(0, 0, TEX_SNOW, {
      x: { min: 0, max: cam.width },
      y: -10,
      lifespan: 6000,
      speedX: { min: -20, max: 20 },
      speedY: { min: 60, max: 120 },
      quantity: 2,
      frequency: 120,
      alpha: { start: 0.85, end: 0.4 },
      scale: { min: 0.5, max: 1.2 },
    });
    this.snow.setScrollFactor(0).setDepth(9100);
  }

  private startFog(): void {
    const cam = this.scene.cameras.main;
    this.fog = this.scene.add
      .tileSprite(0, 0, cam.width, cam.height, TEX_FOG)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(9050)
      .setAlpha(0.65);
  }

  private apply(weather: WeatherKind): void {
    if (this.currentWeather === weather) return;
    this.clearAll();
    this.currentWeather = weather;
    switch (weather) {
      case 'rain':  this.startRain('rain'); break;
      case 'storm': this.startRain('storm'); this.startFog(); break;
      case 'fog':   this.startFog(); break;
      case 'snow':  this.startSnow(); break;
      case 'clear':
      default: break;
    }
  }

  update(timeMs: number, _dtMs: number): void {
    const now = useClockStore.getState().getNow();
    if (!now) return;
    this.apply(now.weather);
    if (this.fog) {
      // slow horizontal scroll for parallax fog feel
      this.fog.tilePositionX += 0.2;
    }
    if (this.currentWeather === 'storm' && timeMs >= this.nextLightning) {
      this.nextLightning = timeMs + 4000 + Math.random() * 8000;
      const cam = this.scene.cameras.main;
      cam.flash(180, 220, 230, 255, false);
      cam.shake(120, 0.003);
    }
  }

  destroy(): void {
    this.scene.scale.off('resize', this.onResize, this);
    this.clearAll();
  }
}
